import React, { createContext, useContext, useRef, useState, useEffect } from 'react';
import { Business, ListingPolicy, ServiceLocation, ShippingPolicy, Country, City } from '../../../data/types/domain';
import { supabase } from '../../../config/supabase';
import { TABLES } from '../../../data/tables';

export interface BusinessListingFormData {
  business?: Omit<Business, 'id' | 'created_at' | 'updated_at'>;
  serviceLocation?: Omit<ServiceLocation, 'id' | 'created_at' | 'updated_at' | 'business_id'>[];
  listingPolicy?: Omit<ListingPolicy, 'id' | 'created_at' | 'updated_at' | 'business_id'>;
  shippingPolicy?: Omit<ShippingPolicy, 'id' | 'business_id' | 'created_at' | 'updated_at'>[];
}

interface FormContextType {
  formData: React.MutableRefObject<BusinessListingFormData>;
  updateFormData: (step: number, data: any) => void;
  resetFormData: () => void;
  countries: Country[];
  cities: City[];
  isLoading: { countries: boolean; cities: boolean };
  getCitiesByCountry: (countryId: number) => City[];
  getCountryName: (id: number) => string;
  getCityName: (id: number) => string;
  addCountry: (country: Country) => void;
  addCity: (city: City) => void;
}

const FormContext = createContext<FormContextType | undefined>(undefined);

export const FormProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const formData = useRef<BusinessListingFormData>({});
  const [countries, setCountries] = useState<Country[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [isLoading, setIsLoading] = useState({ countries: false, cities: false });

  // Fetch countries on component mount
  useEffect(() => {
    const fetchCountries = async () => {
      setIsLoading(prev => ({ ...prev, countries: true }));
      try {
        const { data, error } = await supabase
          .from(TABLES.COUNTRY)
          .select('*')
          .order('name');

        if (error) throw error;
        setCountries(data || []);
      } catch (error) {
        console.error('Error fetching countries:', error);
      } finally {
        setIsLoading(prev => ({ ...prev, countries: false }));
      }
    };

    fetchCountries();
  }, []);

  // Fetch all cities once
  useEffect(() => {
    const fetchAllCities = async () => {
      setIsLoading(prev => ({ ...prev, cities: true }));
      try {
        const { data, error } = await supabase
          .from(TABLES.CITY)
          .select('*')
          .order('name');

        if (error) throw error;
        setCities(data || []);
      } catch (error) {
        console.error('Error fetching cities:', error);
      } finally {
        setIsLoading(prev => ({ ...prev, cities: false }));
      }
    };

    fetchAllCities();
  }, []);

  const updateFormData = (step: number, data: any) => {
    switch (step) {
      case 0:
        formData.current.business = data;
        break;
      case 1:
        formData.current.serviceLocation = data;
        break;
      case 2:
        formData.current.listingPolicy = data;
        break;
      case 3:
        formData.current.shippingPolicy = data;
        break;
    }
  };

  const resetFormData = () => {
    formData.current = {};
  };

  const getCitiesByCountry = (countryId: number): City[] => {
    return cities.filter(city => city.country_id === countryId);
  };

  const getCountryName = (id: number): string => {
    return countries.find(country => country.id === id)?.name || 'Unknown Country';
  };

  const getCityName = (id: number): string => {
    return cities.find(city => city.id === id)?.name || 'Unknown City';
  };

  // Function to add a new country to the context
  const addCountry = (country: Country) => {
    setCountries(prevCountries => {
      // Check if the country already exists to avoid duplicates
      const exists = prevCountries.some(c => c.id === country.id);
      if (exists) return prevCountries;
      return [...prevCountries, country].sort((a, b) => a.name.localeCompare(b.name));
    });
  };

  // Function to add a new city to the context
  const addCity = (city: City) => {
    setCities(prevCities => {
      // Check if the city already exists to avoid duplicates
      const exists = prevCities.some(c => c.id === city.id);
      if (exists) return prevCities;
      return [...prevCities, city].sort((a, b) => a.name.localeCompare(b.name));
    });
  };

  return (
    <FormContext.Provider value={{ 
      formData, 
      updateFormData, 
      resetFormData,
      countries,
      cities,
      isLoading,
      getCitiesByCountry,
      getCountryName,
      getCityName,
      addCountry,
      addCity
    }}>
      {children}
    </FormContext.Provider>
  );
};

export const useFormContext = () => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useFormContext must be used within a FormProvider');
  }
  return context;
}; 